import { useMapAutocomplete } from "@/features/map/useMapAutocomplete"
import { GeoPlace } from "@/features/map/types"
import useEvent from "react-use-event-hook"
import { RView } from "node_modules/rlayers/dist/RMap"
import React, { useCallback, useMemo } from "react"
import { OSMPointerDragEndEvent } from "./Pointer"
import { fromLonLat, toLonLat } from "ol/proj"

export function useOSM(args: {
	initialView: RView
	onMarkerDragEnd?: (event: OSMPointerDragEndEvent) => void
	onSelectedPlaceChange?: (place: GeoPlace) => void
	defaultLatLon?: { lat: number; lon: number }
	defaultAddress?: string
}) {
	const [view, setView] = React.useState<RView>(args.initialView)

	const autocomplete = useMapAutocomplete({
		defaultValue: args.defaultAddress,
		onSelectedPlaceChange: (place) => {
			args.onSelectedPlaceChange?.(place)
			setView((prev) => ({
				...prev,
				center: fromLonLat([place.lon, place.lat]),
			}))
		},
	})

	React.useEffect(() => {
		if (!autocomplete.apiReady) return

		if (args?.defaultLatLon) {
			autocomplete.selectByLatLon(args.defaultLatLon.lat, args.defaultLatLon.lon)
		} else if (args?.defaultAddress) {
			autocomplete.selectByAddress(args.defaultAddress)
		}
	}, [autocomplete.apiReady])

	const onDragEnd = useEvent((event: OSMPointerDragEndEvent) => {
		autocomplete.clearSuggestions()

		const latLon = toLonLat(event.coords).reverse()
		autocomplete.selectByLatLon(latLon[0], latLon[1])
		setView((prev) => ({ ...prev, center: event.coords }))
		if (args?.onMarkerDragEnd) args.onMarkerDragEnd(event)
	})

	const marker = useMemo(() => {
		if (!autocomplete.selectedPlace) return

		return {
			coords: fromLonLat([
				autocomplete.selectedPlace.lon,
				autocomplete.selectedPlace.lat,
			]),
			onDragEnd: onDragEnd,
		}
	}, [autocomplete.selectedPlace, onDragEnd])

	const selectPlace = useCallback((place: GeoPlace) => {
		autocomplete.handleSuggestClick(place)
	}, [])

	return {
		view,
		marker,
		setView,
		selectPlace,
		autocomplete,
		initial: args.initialView,
	}
}
